import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@material-ui/core';
import { CheckCircle, Error, Warning } from '@material-ui/icons';
import ButtonInc from '../../../../../../shared-ui/ButtonInc';

/**
 * Unified amendment progress dialog for all Monamodi amendments
 * Shows 3-step process with individual status indicators:
 * 1. Creating new quote
 * 2. Attaching new quote to policy
 * 3. Saving audit note
 *
 * @param {Object} steps - Status of each step { quote, swap, note }
 *   Each step can be: 'pending' | 'loading' | 'success' | 'error'
 * @param {String} quoteError - Error message from quote creation
 * @param {String} swapError - Error message from quote swap
 * @param {String} noteError - Error message from note save
 * @param {String} successMessage - Amendment-specific success message
 * @param {Function} onViewPolicy - Callback when user clicks View Policy
 * @param {Function} onRetry - Callback when user clicks retry (for full failures)
 */
const AmendmentProgressDialog = ({
  steps = { quote: 'pending', swap: 'pending', note: 'pending' },
  quoteError = null,
  swapError = null,
  noteError = null,
  successMessage = 'The policy has been successfully updated.',
  onViewPolicy,
  onRetry
}) => {
  // Track if the dialog has ever been opened (to prevent closing during state transitions)
  const [hasStarted, setHasStarted] = useState(false);
  const [prevSteps, setPrevSteps] = useState(steps);

  // Determine overall status
  const isProcessing = steps.quote === 'loading' || steps.swap === 'loading' || steps.note === 'loading';
  const isFullSuccess = steps.quote === 'success' && steps.swap === 'success' && steps.note === 'success';
  const isPartialSuccess = steps.quote === 'success' && steps.swap === 'success' && steps.note === 'error';
  const isError = steps.quote === 'error' || steps.swap === 'error';
  const allPending = steps.quote === 'pending' && steps.swap === 'pending' && steps.note === 'pending';

  // Check if we should show the dialog
  const shouldShow = isProcessing || isFullSuccess || isPartialSuccess || isError;

  // Manage dialog visibility
  useEffect(() => {
    // If process starts (any step becomes loading), open the dialog
    if (shouldShow && !hasStarted) {
      setHasStarted(true);
    }

    // Reset hasStarted when all steps go back to pending (after a retry or cancel)
    // But only if they were previously in a non-pending state
    const wasPreviouslyActive = prevSteps.quote !== 'pending' || prevSteps.swap !== 'pending' || prevSteps.note !== 'pending';
    if (allPending && wasPreviouslyActive && hasStarted) {
      setHasStarted(false);
    }

    setPrevSteps(steps);
  }, [steps, shouldShow, hasStarted, allPending, prevSteps]);

  // Show dialog if process has started
  const showDialog = hasStarted;

  // Get the main error message (from quote or swap failure)
  const mainError = quoteError || swapError;

  // Render step indicator
  const renderStepIndicator = (stepStatus) => {
    switch (stepStatus) {
      case 'loading':
        return <CircularProgress size={20} style={{ color: '#032A37' }} />;
      case 'success':
        return <CheckCircle style={{ color: '#28a745', fontSize: '20px' }} />;
      case 'error':
        return <Error style={{ color: '#dc3545', fontSize: '20px' }} />;
      default:
        return <div style={{ width: '20px', height: '20px', border: '2px solid #ddd', borderRadius: '50%' }} />;
    }
  };

  // Render step text
  const renderStepText = (stepStatus, label) => {
    const color = stepStatus === 'success' ? '#28a745' : stepStatus === 'error' ? '#dc3545' : '#666';
    const statusText = stepStatus === 'success' ? 'Done' : stepStatus === 'loading' ? 'In Progress...' : stepStatus === 'error' ? 'Failed' : '';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        {renderStepIndicator(stepStatus)}
        <span style={{ fontSize: '14px', color }}>
          {label} {statusText && <span style={{ fontWeight: '500' }}>- {statusText}</span>}
        </span>
      </div>
    );
  };

  // Don't show dialog if no processing has started
  if (!showDialog) {
    return null;
  }

  // Render content based on current state
  let dialogContent;

  if (isProcessing) {
    // Processing state
    dialogContent = (
      <div style={{ padding: '20px' }}>
        <h2 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: 'bold', color: '#032A37', textAlign: 'center' }}>
          Processing Amendment
        </h2>
        {renderStepText(steps.quote, 'Creating new quote')}
        {renderStepText(steps.swap, 'Attaching new quote to policy')}
        {renderStepText(steps.note, 'Saving audit note')}
      </div>
    );
  } else if (isFullSuccess) {
    // Full success state
    dialogContent = (
      <div style={{ padding: '20px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 'bold', color: '#28a745', textAlign: 'center' }}>
          Amendment Successful
        </h2>
        <p style={{ marginBottom: '20px', textAlign: 'center' }}>{successMessage}</p>
        <div style={{ marginBottom: '20px' }}>
          {renderStepText('success', 'Creating new quote')}
          {renderStepText('success', 'Attaching new quote to policy')}
          {renderStepText('success', 'Saving audit note')}
        </div>
        <div style={{ textAlign: 'center' }}>
          <ButtonInc onClick={onViewPolicy} style={{ background: '#032A37' }}>
            View Policy
          </ButtonInc>
        </div>
      </div>
    );
  } else if (isPartialSuccess) {
    // Partial success state (note save failed)
    dialogContent = (
      <div style={{ padding: '20px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 'bold', color: '#856404', textAlign: 'center' }}>
          Amendment Partially Complete
        </h2>
        <div style={{ marginBottom: '20px' }}>
          {renderStepText('success', 'Creating new quote')}
          {renderStepText('success', 'Attaching new quote to policy')}
          {renderStepText('error', 'Saving audit note')}
        </div>

        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          <Warning style={{ color: '#856404', fontSize: '24px', flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, fontSize: '14px', color: '#856404', fontWeight: '600', marginBottom: '8px' }}>
              The policy has been successfully updated, but the audit note could not be saved.
            </p>
            {noteError && (
              <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                Error: {noteError}
              </p>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <ButtonInc onClick={onViewPolicy} style={{ background: '#032A37' }}>
            View Policy
          </ButtonInc>
        </div>
      </div>
    );
  } else if (isError) {
    // Error state (quote or swap failed)
    dialogContent = (
      <div style={{ padding: '20px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 'bold', color: '#dc3545', textAlign: 'center' }}>
          Error Processing Amendment
        </h2>
        <div style={{ marginBottom: '20px' }}>
          {renderStepText(steps.quote, 'Creating new quote')}
          {renderStepText(steps.swap, 'Attaching new quote to policy')}
          {renderStepText(steps.note, 'Saving audit note')}
        </div>

        {mainError && (
          <p style={{ marginBottom: '20px', color: '#721c24', fontSize: '14px', textAlign: 'center' }}>
            {mainError}
          </p>
        )}

        <div style={{ textAlign: 'center' }}>
          <ButtonInc onClick={onRetry} style={{ background: '#dc3545' }}>
            Try Again
          </ButtonInc>
        </div>
      </div>
    );
  }

  // Single Dialog that stays mounted and only updates content
  return (
    <Dialog open={showDialog} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogContent>
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
};

export default AmendmentProgressDialog;
